import React, { FC, useCallback, useEffect, useRef } from "react";
import _ from "lodash";
import swecomapUtils from "../../../utils/swecomap";

import { useSetMapClient } from "../../../App";

import Client from "swecomap/src/swecomap/Client";
import { Config } from "swecomap/src/swecomap/config/Config";
import { useStableValue } from "../../../hooks/useStableValue";
import { useOfflineMapId } from "../../../state";
import { makeOfflineTileSource, useOfflineMaps } from "../../../hooks/useOfflineMaps";

/**
 * React wrapper for Swecomap
 *
 * Automatically re-creates Swecomap if the `config` property changes
 */
const Swecomap: FC<{ config: Config; onLoaded: (client: Client) => void; visible: boolean }> = ({ config, onLoaded, visible }) => {
  const ref = useRef<HTMLDivElement | null>(null);

  const activeConfig = useStableValue(config);

  const clientRef = useRef<Client | null>(null);

  const setClient = useSetMapClient();
  useEffect(() => {
    if (!activeConfig || Object.keys(activeConfig).length === 0) {
      return () => {};
    }

    const target = ref.current;

    const config = _.cloneDeep({ ...activeConfig, target });

    const client = (clientRef.current = new Client({
      config,
      callback: (client) => {
        swecomapUtils.setClient(client);
        setClient!(client);
        onLoaded(client);
      },
    }));

    return () => {
      target!.innerHTML = "";
      if (client.map) {
        client.map.setTarget(null as any);
      }
    };
  }, [activeConfig, onLoaded, setClient]);

  const offlineMapId = useOfflineMapId();
  const offlineMaps = useOfflineMaps();
  const updateOfflineMap = useCallback(async () => {
    const download = offlineMaps.downloads.find((d) => d.id === offlineMapId);
    const client = clientRef.current;
    if (client) {
      const offlineLayer = client.map
        .getLayers()
        .getArray()
        .find((l) => l.get("name") === "Offline");

      if (download && download.id && download.status !== "downloaded") {
        const layer = (offlineLayer as ol.layer.Layer) || new ol.layer.Tile({ name: "Offline" } as any);
        layer.setSource(await makeOfflineTileSource(download.id));
        if (!offlineLayer) {
          client.map.addLayer(layer);
        }

        const view = client.map.getView();
        view.setZoom(download.startZoom);
        view.setCenter(ol.extent.getCenter(download.extent));
      } else if (offlineLayer) {
        client.map.removeLayer(offlineLayer);
      }
    }
  }, [offlineMapId, offlineMaps.downloads]);

  useEffect(() => {
    updateOfflineMap().then(() => {});
  }, [offlineMapId, offlineMaps, updateOfflineMap]);

  return <div ref={ref} style={{ visibility: visible ? "visible" : "hidden" }} />;
};

export default Swecomap;
